"use client";

import styles from "@/styles/product-detail/customer-reviews.module.scss";
import Image from "next/image";
import Link from "next/link";
import { Rate, ImagePreview } from "react-vant";
import { Arrow } from "@react-vant/icons";
import { useState, useEffect, useCallback, useRef } from "react";
import productApi from "@/service/modules/productApi";
import type { CustomerReviewData, ReviewItem } from "@/types/product";

interface Props {
  productId: number | string;
}

const defaultCustomerData: CustomerReviewData = {
  review: [],
  reviewAvgRank: 0,
  reviewCount: 0,
  reviewSummary: {
    rating1: 0,
    rating2: 0,
    rating3: 0,
    rating4: 0,
    rating5: 0,
  },
};

export default function ProductCustomerReviews({ productId }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasRequestedRef = useRef(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [customerData, setCustomerData] = useState<CustomerReviewData>(defaultCustomerData);

  const getReviews = useCallback(async () => {
    try {
      const res = await productApi.getCustomerReviews({
        productID: productId,
        sort: "",
        start: 1,
        rows: 3,
      });
      if (res.data) {
        const newData = res.data;
        setCustomerData(newData);
      }
    } catch {
      console.log("fetch customer reviews error");
    }
  }, [productId]);

  useEffect(() => {
    const target = sectionRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getReviews();
  }, [shouldLoad, getReviews]);

  return (
    <div ref={sectionRef}>
      {customerData && customerData.reviewCount ? (
        <div className={styles["comp-customer-reviews"]}>
          <div className={styles["reviews-card"]}>
            <div className={styles["title-box"]}>
              <div className={styles["title"]}>Ratings & Reviews</div>

              <div className={styles["rank-box"]}>
                <Rate
                  value={customerData.reviewAvgRank}
                  count={5}
                  voidColor={"#dedede"}
                  allowHalf
                  readOnly
                />
                <Link 
                  href={`/product/${productId}/reviews`}
                  className={styles["product-rate-count"]}
                >
                  ({customerData.reviewCount})
                </Link>
              </div>
            </div>

            {customerData && customerData.review ? (
              <>
                <div className={styles["reviews-list"]}>
                  {customerData.review.map((item: ReviewItem, index: number) => {
                    if (!item.detail) {
                      return null;
                    }
                    return (
                      <div className={styles["reviews-item"]} key={index}>
                        <div className={styles["review-header"]}>
                          <div className={styles["avatar"]} aria-hidden="true">
                            <span className={styles["avatar-head"]}></span>
                            <span className={styles["avatar-body"]}></span>
                          </div>

                          <div className={styles["review-meta"]}>
                            <div className={styles["user-name"]}>
                              {item.customerName || "customer"}
                            </div>
                            <div className={styles["review-time"]}>
                              {item.createTime}
                            </div>
                          </div>

                          <div className={styles["review-rate"]}>
                            <Rate
                              value={item.rate}
                              count={5}
                              color={"#FDC92E"}
                              voidColor={"#dedede"}
                              allowHalf
                              readOnly
                              size={16}
                            />
                          </div>
                        </div>

                        <div className={styles["review-content"]}>
                          {item.detail}
                          {!!(item.imgArray && item.imgArray.length > 0) && (
                            <div className={styles["img-list"]}>
                              {item.imgArray.map((img: string) => (
                                <div
                                  className={styles["comment-img"]}
                                  key={img}
                                >
                                  <Image
                                    src={img}
                                    width={100}
                                    height={100}
                                    alt={img}
                                    onClick={() =>
                                      ImagePreview.open({
                                        images: item.imgArray,
                                        onChange: (index) =>
                                          console.log(
                                            `当前展示第${index + 1}张`
                                          ),
                                      })
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Link
                  href={`/product/${productId}/reviews`}
                  className={styles["view-all"]}
                >
                  View All Reviews{" "}
                  <Arrow
                    style={{ marginTop: "2px", marginLeft: "4px" }}
                  ></Arrow>
                </Link>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
